import { supabase } from "../lib/supabase";

/**
 * Sanitize filename to prevent special characters and spaces
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return `file_${Date.now()}`; // Handle undefined or null file names
  
  return fileName
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/[^\w.-]/g, "") // Remove any non-alphanumeric characters except dots and hyphens
    .normalize("NFD") // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics
};

/**
 * Generate a file path for commercial invoice using company ID as the root folder
 */
export const generateCompanyBasedPath = (
  companyId: number | string,
  bookingId: number | string,
  fileName: string
): string => {
  // Ensure we have valid values for all parameters
  const safeCompanyId = companyId || 0;
  const safeBookingId = bookingId || 0;
  
  // Sanitize the filename
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create path with company_id as root
  return `${safeCompanyId}/${safeBookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Upload a commercial invoice file using the company-based path structure
 */
export const uploadCommercialInvoice = async (
  bookingId: number | string,
  file: File, 
  appendExisting: boolean = false,
  companyId: number | string = 0
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      console.error("No file provided for upload");
      return { error: "No file provided" };
    }
    
    console.log("uploadCommercialInvoice starting with:", {
      bookingId,
      companyId,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      appendExisting
    });
    
    // Get company ID from booking if not provided
    const finalCompanyId = companyId || 0; // Use 0 as default company ID if not provided
    
    // Generate path with company ID as root
    const filePath = generateCompanyBasedPath(finalCompanyId, bookingId, file.name);
    
    console.log("Generated file path:", filePath);
    
    // Upload to Supabase storage
    console.log("Uploading to bucket:", "commercial_invoices");
    const { data, error } = await supabase.storage
      .from("commercial_invoices")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
    
    if (error) {
      console.error("Error uploading commercial invoice:", error);
      return { error };
    }
    
    console.log("Commercial invoice uploaded successfully:", data.path);
    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadCommercialInvoice:", error);
    return { error };
  }
};

/**
 * Get public URL for a commercial invoice file
 */
export const getCommercialInvoicePublicUrl = (
  path: string
): string | null => {
  try {
    if (!path || path.trim() === '') {
      return null;
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    const { data } = supabase.storage
      .from("commercial_invoices")
      .getPublicUrl(cleanPath);
    
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting public URL:", error);
    return null;
  }
};

/**
 * Get public URLs for multiple commercial invoice files
 */
export const getCommercialInvoicePublicUrls = (
  pathsString: string
): string[] => {
  if (!pathsString || pathsString.trim() === '') {
    return [];
  }
  
  // Split by comma and process each path
  const paths = pathsString.split(",");
  return paths.map(path => {
    const url = getCommercialInvoicePublicUrl(path);
    return url || "";
  }).filter(url => url !== "");
};

/**
 * Create a signed URL for commercial invoice file (for download)
 */
export const createCommercialInvoiceSignedUrl = async (
  path: string,
  expiresIn: number = 3600
): Promise<string | null> => {
  try {
    if (!path || path.trim() === '') {
      return null;
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    const { data, error } = await supabase.storage
      .from("commercial_invoices")
      .createSignedUrl(cleanPath, expiresIn, {
        download: true,
      });
    
    if (error) {
      console.error("Error creating signed URL:", error);
      return null;
    }
    
    return data.signedUrl;
  } catch (error) {
    console.error("Error in createCommercialInvoiceSignedUrl:", error);
    return null;
  }
};

/**
 * Create signed URLs for multiple commercial invoice files
 */
export const createCommercialInvoiceSignedUrls = async (
  pathsString: string,
  expiresIn: number = 3600
): Promise<(string | null)[]> => {
  if (!pathsString || pathsString.trim() === '') {
    return [];
  }
  
  // Split by comma and process each path
  const paths = pathsString.split(",");
  
  // Process all paths concurrently
  const promises = paths.map(path => createCommercialInvoiceSignedUrl(path, expiresIn));
  return Promise.all(promises);
};

/**
 * Download a commercial invoice file
 */
export const downloadCommercialInvoice = async (
  path: string,
  filename: string
): Promise<void> => {
  try {
    if (!path || path.trim() === '') {
      throw new Error("Invalid file path provided");
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    console.log("Downloading commercial invoice:", cleanPath);
    
    // Get the file extension
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";
    
    // Create a signed URL for download
    const signedUrl = await createCommercialInvoiceSignedUrl(cleanPath);
    
    if (!signedUrl) {
      throw new Error("Could not generate download link");
    }
    
    // Fetch the file content
    const response = await fetch(signedUrl);
    const blob = await response.blob();
    
    // Create a download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${filename}.${fileExt}`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log("Download completed for:", filename);
  } catch (error) {
    console.error("Error downloading commercial invoice:", error);
    throw error;
  }
}; 